import React from 'react'
import { useSelector } from 'react-redux';
import { icons } from '../../../assets';

const formatedDate = (date) => {
    if (!date) return 'Sin fecha';
    const fecha = new Date(date);
    return fecha.toLocaleDateString('es-ES', {
        year: '2-digit',
        month: '2-digit',
        day: '2-digit',
    }).replace(/\//g, '.');
}


const LineBlock = () => <div className='w-full h-[1px] bg-[#E7EDFF] mb-2' />
const TitleBlock = ({ text = "" }) => <p className='text-[#282828] font-bold text-base leading-5 mb-4'>{text}</p>

const TOTAL_EVALUATIONS = 3

// Cada sesión trae un objeto "evaluaciones" con entradas de dos tipos posibles:
// lecciones (identificadas por lessonId) y evaluaciones (identificadas por evaluationId).
// Se agrupan por su id porque la misma lección/evaluación puede repetirse entre sesiones.
// Para lecciones: título = item.title (actividad), subtítulo = item.module, fecha = la más
// reciente entre repeticiones, intentos = cantidad de repeticiones encontradas.
const buildLessonsGrouped = (dataFilterPerUser = []) => {
    const lessonsMap = new Map()

    dataFilterPerUser.forEach((session) => {
        const evaluaciones = session?.evaluaciones || {}
        Object.values(evaluaciones).forEach((item) => {
            if (!item || !item.lessonId) return
            const existing = lessonsMap.get(item.lessonId)
            if (!existing) {
                lessonsMap.set(item.lessonId, {
                    lessonId: item.lessonId,
                    title: item.title,
                    subtitle: item.module,
                    date: item.performedAtUtc,
                    attempts: 1,
                })
                return
            }
            existing.attempts += 1
            if (item.performedAtUtc && (!existing.date || new Date(item.performedAtUtc) > new Date(existing.date))) {
                existing.date = item.performedAtUtc
            }
        })
    })

    return Array.from(lessonsMap.values())
}

const buildSummaryStats = (dataFilterPerUser = []) => {
    const lessonsGrouped = buildLessonsGrouped(dataFilterPerUser)
    const evaluationsMap = new Map()
    let totalSeconds = 0

    dataFilterPerUser.forEach((session) => {
        const evaluaciones = session?.evaluaciones || {}
        Object.values(evaluaciones).forEach((item) => {
            if (!item || !item.evaluationId) return
            evaluationsMap.set(item.evaluationId, item)
                ; (item.sections || []).forEach((section) => {
                    totalSeconds += Number(section?.sectionTimeSeconds) || 0
                })
        })
    })

    const hours = Math.floor(totalSeconds / 3600)
    const minutes = Math.floor((totalSeconds % 3600) / 60)
    const tiempoTotal = hours > 0 ? `${hours}h${minutes ? ` ${minutes}m` : ""}` : `${minutes}m`

    const stats = {
        cursosRealizados: lessonsGrouped.length,
        evaluacionesRealizadas: `${evaluationsMap.size}/${TOTAL_EVALUATIONS}`,
        tiempoTotal,
        totalSeconds,
        lecciones: lessonsGrouped,
        evaluaciones: Array.from(evaluationsMap.values()),
    }

    console.log('Resumen de capacitaciones - agrupación:', stats)

    return stats
}

// Resultado de paso: 1 Correct, 2 Incorrect, 3 NotPerformed.
// Las "secciones" (sectionId/sectionName) NO son la agrupación que se muestra en el
// detalle -- solo sirven para el tiempo total (sectionTimeSeconds es por sección).
// Lo que se muestra como "Detalles: X" es por categoryId del paso: cada paso solo
// puede caer en dos categorías posibles, 0=Proceso operativo, 1=Prácticas seguras
// (puede traer una o ambas en su categoryIds). Una categoría queda "Aprobada" cuando
// el % de pasos correctos (result === 1) sobre el total de pasos de esa categoría es
// >= 80%, igual que el requiredPercentage que ya trae Unity en categoryResults.
const EVALUATION_CATEGORY_CATALOG = [
    { categoryId: "0", label: "Proceso operativo" },
    { categoryId: "1", label: "Prácticas seguras" },
]
const CATEGORY_APPROVAL_THRESHOLD = 0.8

// Agrupa por evaluationId (puede repetirse entre sesiones). Secciones y pasos se
// fusionan por su id (sectionId / stepId) ya que una evaluación puede completarse
// por partes en sesiones distintas.
const buildEvaluationsGrouped = (dataFilterPerUser = []) => {
    const evaluationsMap = new Map()

    dataFilterPerUser.forEach((session) => {
        const evaluaciones = session?.evaluaciones || {}
        Object.values(evaluaciones).forEach((item) => {
            if (!item || !item.evaluationId) return

            const existing = evaluationsMap.get(item.evaluationId) || {
                evaluationId: item.evaluationId,
                title: item.title,
                date: item.performedAtUtc,
                attemptNumber: item.attemptNumber,
                sectionsMap: new Map(),
                stepsMap: new Map(),
            }

            if (item.performedAtUtc && (!existing.date || new Date(item.performedAtUtc) > new Date(existing.date))) {
                existing.date = item.performedAtUtc
            }
            if (Number(item.attemptNumber) > Number(existing.attemptNumber || 0)) {
                existing.attemptNumber = item.attemptNumber
            }

            ;(item.sections || []).forEach((section) => {
                existing.sectionsMap.set(section.sectionId, section)
                ;(section.steps || []).forEach((step) => {
                    existing.stepsMap.set(step.stepId, step)
                })
            })

            evaluationsMap.set(item.evaluationId, existing)
        })
    })

    return Array.from(evaluationsMap.values()).map((evaluation) => {
        const sections = Array.from(evaluation.sectionsMap.values())
        const totalSeconds = sections.reduce((sum, section) => sum + (Number(section.sectionTimeSeconds) || 0), 0)

        const steps = Array.from(evaluation.stepsMap.values())
        const totalPoints = steps.reduce((sum, step) => sum + (Number(step.pointsObtained) || 0), 0)

        const categoriesUI = EVALUATION_CATEGORY_CATALOG
            .map(({ categoryId, label }) => {
                const categorySteps = steps.filter((step) =>
                    (step.categoryIds || []).some((id) => String(id) === categoryId)
                )
                const total = categorySteps.length
                // Number(...) porque el JSON real de Unity trae result como string ("1"),
                // no como número -- una comparación estricta contra 1 nunca haría match.
                const correctCount = categorySteps.filter((step) => Number(step.result) === 1).length
                const approvalRate = total > 0 ? correctCount / total : 0

                return {
                    title: label,
                    total: `${Math.round(approvalRate * 100)}%`,
                    approved: total > 0 && approvalRate >= CATEGORY_APPROVAL_THRESHOLD,
                    steps: categorySteps.map((step) => ({
                        name: step.stepName,
                        correct: Number(step.result) === 1,
                        points: `${Number(step.pointsObtained) || 0} pts`,
                    })),
                }
            })
            .filter((category) => category.steps.length > 0)

        const decisionScore = categoriesUI.length > 0
            ? categoriesUI.reduce((sum, category) => sum + parseInt(category.total, 10), 0) / categoriesUI.length
            : 0

        const hours = Math.floor(totalSeconds / 3600)
        const minutes = Math.floor((totalSeconds % 3600) / 60)
        const tiempoLabel = hours > 0 ? `${hours}h ${minutes}m` : `${minutes} min`

        return {
            title: evaluation.title,
            date: evaluation.date,
            stats: [
                { value: `${Math.round(decisionScore)}%`, label: "Toma de decisiones correctas" },
                { value: `${totalPoints} pts`, label: "Puntos obtenidos" },
                { value: tiempoLabel, label: "Tiempo total" },
                { value: `${evaluation.attemptNumber ?? 1}`, label: "Intentos realizados" },
            ],
            sections: categoriesUI,
        }
    })
}

const Summary = ({ dataFilterPerUser }) => {
    const { cursosRealizados, evaluacionesRealizadas, tiempoTotal } = buildSummaryStats(dataFilterPerUser)

    return (
        <div className='w-full px-6 pb-4'>
            <LineBlock />
            <TitleBlock text={"Resumen de capacitaciones"} />
            <div className='flex gap-3'>
                <SummaryCard icon={icons.book} value={cursosRealizados} label={"Cursos realizados"} />
                <SummaryCard icon={icons.board} value={evaluacionesRealizadas} label={"Evaluaciones realizadas"} />
                <SummaryCard icon={icons.clock} value={tiempoTotal} label={"Tiempo total"} />
            </div>
        </div>
    )
}

const SummaryCard = ({ icon, value, label }) => {
    return (
        <div className='flex-1 flex gap-2 bg-[#F8FAFC] rounded-xl px-4 py-3 border-solid border-2 border-[#E7EDFF]'>
            <div className='w-5 h-5 mt-1.5'>
                <img src={icon} className='w-full h-full object-contain' alt="" />
            </div>
            <div>
                <p className='text-[#111827] font-semibold text-lg'>{value}</p>
                <p className='text-[#808080] text-sm'>{label}</p>
            </div>
        </div>
    )
}


const CarouselArrow = ({ direction = "right", onClick }) => (
    <button type="button" onClick={onClick} className='w-8 h-8 rounded-lg border-solid border border-[#E7EDFF] flex justify-center items-center bg-white'>
        <img src={icons.mayorqueicon} className={`w-2 h-3 ${direction === "left" ? "rotate-180" : ""}`} alt="" />
    </button>
)

const LessonCard = ({ title, subtitle, date, attempts }) => {
    return (
        <div className='flex-none w-[300px] bg-white rounded-xl px-4 py-3 border-solid border-2 border-[#E7EDFF]'>
            <div className='mb-3'>
                <p className='text-[#00144F] font-semibold text-sm leading-5'>{title}</p>
                <p className='text-[#1D1A34] text-xs mt-1'>{subtitle}</p>
            </div>
            <div className='flex items-center justify-between'>
                <div className='flex items-center gap-1 text-xs text-[#191919]'>
                    <div className='w-4 h-4'>
                        <img src={icons.calendar} className='w-full h-full object-contain' alt="" />
                    </div>
                    Fecha: {formatedDate(date)}
                </div>
                <div className='flex items-center gap-1 text-xs text-[#191919]'>
                    <div className='w-4 h-4'>
                        <img src={icons.arrowRepeat} className='w-full h-full object-contain' alt="" />
                    </div>
                    {attempts} intentos
                </div>
            </div>
        </div>
    )
}

const LESSON_CARD_WIDTH = 300
const LESSON_CARD_GAP = 12

const LessonsRealized = ({ lessons = [] }) => {
    const scrollRef = React.useRef(null)

    const handleScroll = (direction) => {
        if (!scrollRef.current) return
        const offset = LESSON_CARD_WIDTH + LESSON_CARD_GAP
        scrollRef.current.scrollBy({ left: direction === "left" ? -offset : offset, behavior: "smooth" })
    }

    return (
        <div className='w-full px-6 pb-4'>
            <LineBlock />
            <div className='flex items-center justify-between'>
                <TitleBlock text={"Cursos realizados"} />
                <div className='flex gap-2 mb-4'>
                    <CarouselArrow direction="left" onClick={() => handleScroll("left")} />
                    <CarouselArrow direction="right" onClick={() => handleScroll("right")} />
                </div>
            </div>
            <div ref={scrollRef} className='flex gap-3 overflow-x-hidden scroll-smooth'>
                {lessons.map((lesson, index) => (
                    <LessonCard key={index} {...lesson} />
                ))}
            </div>
        </div>
    )
}



const EvaluationStat = ({ value, label, isFirst }) => (
    <div className={isFirst ? "" : "border-l border-solid border-[#E7EDFF] pl-3"}>
        <p className='text-[#0D46F2] font-bold text-sm'>{value}</p>
        <p className='text-[#00144F] text-xs mt-0.5'>{label}</p>
    </div>
)

const EvaluationStepRow = ({ name, correct, points }) => (
    <div className='grid grid-cols-[1fr_120px_70px] items-center px-4 py-3 border-t border-solid border-[#E7EDFF] first:border-t-0'>
        <span className='text-sm text-[#282828]'># {name}</span>
        <span className='flex items-center gap-1 text-xs'>
            <div className={`w-4 h-4 rounded-full flex items-center justify-center ${correct ? "bg-[#EAFFE0]" : "bg-[#FFC2C2]"}`}>
                {correct ? (
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M10 3L4.5 8.5L2 6" stroke="#48AA18" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                ) : (
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M3 9L9 3M3 3L9 9" stroke="#E52E2E" strokeWidth="0.9" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                )}
            </div>
            {/* {correct ? <CheckIcon /> : <CrossIcon />} */}
            <span className={correct ? "text-[#174400]" : "text-[#5D0000]"}>{correct ? "Correcto" : "Incorrecto"}</span>
        </span>
        <span className='text-xs text-[#282828] text-right'>{points}</span>
    </div>
)

const EvaluationSection = ({ title, total, approved, steps = [] }) => (
    <div>
        <div className='flex items-center justify-between px-4 pt-3 pb-2'>
            <div>
                <p className='text-[#282828] font-semibold text-sm'>Detalles: {title}</p>
                <p className='text-[#4070FF] text-xs font-semibold mt-0.5'>Total: {total}</p>
            </div>
            <span className={`flex items-center gap-1 text-xs px-2 py-1 rounded-full ${approved ? "bg-[#EAFFE0] text-[#48AA18]" : "bg-[#FFF1F2] text-[#C70036]"}`}>
                <span className={`w-1.5 h-1.5 rounded-full bg-current ${approved ? "text-[#48AA18]" : "text-[#C70036]"}`} />
                {approved ? "Aprobado" : "No aprobado"}
            </span>
        </div>
        <div className='grid grid-cols-[1fr_120px_70px] items-center bg-[#0D46F2] px-4 py-2 text-white text-xs font-semibold'>
            <span># Paso</span>
            <span>Estado</span>
            <span className='text-right'>Puntos</span>
        </div>
        <div>
            {steps.map((step, index) => (
                <EvaluationStepRow key={index} {...step} />
            ))}
        </div>
    </div>
)

const EvaluationCard = ({ title, date, stats = [], sections = [] }) => {
    const [open, setOpen] = React.useState(true)

    return (
        <div className='rounded-xl overflow-hidden border-solid border-2 border-[#E7EDFF] mb-3'>
            <div className='w-full flex items-center justify-between bg-[#0D46F2] px-4 py-3'>
                <span className='text-white font-semibold text-sm'>{title}</span>
                <span className='flex items-center gap-1 text-white text-xs'>
                    <div className='w-4 h-4'>
                        <img src={icons.calendar} className='w-full h-full object-contain brightness-0 invert' alt="" />
                    </div>
                    Fecha: {formatedDate(date)}
                </span>
            </div>
            <div className='grid grid-cols-4 gap-3 px-4 py-3 border-b-2 border-solid border-[#E7EDFF]'>
                {stats.map((stat, index) => (
                    <EvaluationStat key={index} {...stat} isFirst={index === 0} />
                ))}
            </div>
            {open && (
                <div className='max-h-[220px] overflow-y-auto'>
                    {sections.map((section, index) => (
                        <EvaluationSection key={index} {...section} />
                    ))}
                </div>
            )}
            <button
                type="button"
                onClick={() => setOpen((prev) => !prev)}
                className='w-full flex items-end justify-end gap-1 py-2 px-5 bg-white text-[#0D46F2] text-xs font-semibold'
            >
                {open ? "Ver menos" : "Ver detalles"}
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className={`transition-transform ${open ? "" : "rotate-180"}`}>
                    <path d="M6 15l6-6 6 6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            </button>
        </div>
    )
}

const EvaluationsRealized = ({ date = "Sin fecha", evaluations = [] }) => {
    const [currentIndex, setCurrentIndex] = React.useState(0)

    const handlePage = (direction) => {
        setCurrentIndex((prev) => {
            if (direction === "left") return prev === 0 ? evaluations.length - 1 : prev - 1
            return prev === evaluations.length - 1 ? 0 : prev + 1
        })
    }

    const currentEvaluation = evaluations[currentIndex]

    return (
        <div className='w-full px-6 pb-4'>
            <LineBlock />
            <TitleBlock text={"Evaluaciones realizadas"} />
            {currentEvaluation && (
                <EvaluationCard date={date} {...currentEvaluation} />
            )}
            {evaluations.length > 1 && (
                <div className='flex items-center justify-center gap-4'>
                    <CarouselArrow direction="left" onClick={() => handlePage("left")} />
                    <p className='text-sm font-semibold text-[#282828]'>{currentIndex + 1} de {evaluations.length}</p>
                    <CarouselArrow direction="right" onClick={() => handlePage("right")} />
                </div>
            )}
        </div>
    )
}

const Header = ({ nameUser, matricula, dateTime }) => {
    const iniciales = nameUser ? nameUser.split(" ").map((word) => word[0]).slice(0, 2).join("").toUpperCase() : "";

    return (
        <div className='w-full py-4 px-6 flex'>
            <div className=''>
                <div className='w-10 h-10 rounded-full flex justify-center items-center bg-[#EDE9FE] mr-3'>
                    <p className='text-[#00144F] font-semibold text-xs'>{iniciales}</p>
                </div>
            </div>
            <div className='flex flex-col gap-2'>
                <div>
                    <p className='text-[#282828] font-semibold text-sm leading-5'>{nameUser}</p>
                </div>
                <div>
                    <div className='flex items-center gap-1 text-xs text-[#808080]'>
                        <span>ID: {matricula} · Operador</span>
                        <span className='flex items-center gap-1 ml-2'>
                            <img src={icons.initialDate} className='w-4 h-4' alt="icono de fecha" />
                            Fecha de registro: {formatedDate(dateTime)}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    )
}

// eslint-disable-next-line
export const UserPerfilColgateMontacarga = ({ grupo, nameUser, matricula, Fecha_y_hora }) => {
    const { arrayData } = useSelector((state) => state.table);

    const dataFilterPerUser = arrayData.filter((obj) => obj.matricula === matricula);

    console.log(dataFilterPerUser)

    const dataLesson = buildLessonsGrouped(dataFilterPerUser)

    const dataEvaluations = buildEvaluationsGrouped(dataFilterPerUser)

    return (
        <div className='w-full'>
            <Header nameUser={nameUser} matricula={matricula} dateTime={"2023-08-30"} />
            <Summary dataFilterPerUser={dataFilterPerUser} />
            <LessonsRealized lessons={dataLesson} />
            <EvaluationsRealized evaluations={dataEvaluations} />
        </div>
    )
}

export default UserPerfilColgateMontacarga
